import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Stethoscope, CheckCircle, AlertTriangle, XCircle, FileSearch, Brain } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface MedicalValidation {
  overall: "accurate" | "questionable" | "inaccurate";
  confidence: number;
  factChecks: {
    claim: string;
    status: "verified" | "questionable" | "incorrect";
    explanation: string;
  }[];
  terminologyCheck: {
    correct: string[];
    incorrect: string[];
    suggestions: string[];
  };
  guidelinesCompliance: {
    standard: string;
    compliant: boolean;
    issues: string[];
  }[];
  recommendations: string[];
}

export default function MedicalAccuracyValidator() {
  const { toast } = useToast();
  const { session } = useAuth();
  const [content, setContent] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [validation, setValidation] = useState<MedicalValidation | null>(null);

  const { data: recentValidations, refetch: refetchValidations } = useQuery({
    queryKey: ['recent-medical-validations'],
    queryFn: async () => {
      const { data } = await supabase
        .from('medical_validations')
        .select('*')
        .order('validated_at', { ascending: false })
        .limit(10);
      return data || [];
    },
  });

  const validateContent = async () => {
    if (!content.trim()) {
      toast({
        title: "No Content",
        description: "Please enter medical content to validate.",
        variant: "destructive"
      });
      return;
    }

    setIsValidating(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('ai-medical-validator', {
        body: {
          content: content,
          userId: session?.user?.id
        }
      });

      if (error) throw error;
      
      setValidation(data.validation);
      refetchValidations(); // Refresh history
      
      toast({
        title: "Validation Complete",
        description: "Medical content has been validated successfully.",
      });
    } catch (error) {
      console.error('Error validating content:', error);
      toast({
        title: "Validation Failed",
        description: "Failed to validate medical content. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsValidating(false);
    }
  };

  const loadSampleContent = () => {
    setContent(`Hypertension Treatment Guidelines:

1. First-line therapy for uncomplicated hypertension includes:
   - ACE inhibitors (e.g., lisinopril 10-20mg daily)
   - ARBs (e.g., losartan 50-100mg daily)
   - Thiazide diuretics (e.g., hydrochlorothiazide 25mg daily)
   - Calcium channel blockers (e.g., amlodipine 5-10mg daily)

2. Target blood pressure for most adults: <130/80 mmHg

3. Lifestyle modifications:
   - DASH diet (low sodium, high potassium)
   - Regular exercise (150 minutes moderate intensity per week)
   - Weight management (BMI 18.5-24.9)
   - Limit alcohol consumption

4. Patient monitoring:
   - Blood pressure checks every 3-6 months
   - Annual kidney function assessment
   - Electrolyte monitoring with diuretic use`);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "accurate":
      case "verified":
        return <CheckCircle className="h-4 w-4 text-success" />;
      case "questionable":
        return <AlertTriangle className="h-4 w-4 text-warning" />;
      case "inaccurate":
      case "incorrect":
        return <XCircle className="h-4 w-4 text-destructive" />;
      default:
        return <FileSearch className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "accurate":
      case "verified":
        return "success";
      case "questionable":
        return "warning";
      case "inaccurate":
      case "incorrect":
        return "destructive";
      default:
        return "secondary";
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return "text-success";
    if (confidence >= 75) return "text-primary";
    if (confidence >= 60) return "text-warning";
    return "text-destructive";
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Medical Accuracy Validator</h3>
          <p className="text-sm text-muted-foreground">
            AI-powered validation of medical content against current guidelines and evidence
          </p>
        </div>
      </div>

      <Alert>
        <Stethoscope className="h-4 w-4" />
        <AlertDescription>
          <strong>Medical Disclaimer:</strong> This tool provides AI-generated analysis for educational purposes only. 
          Always consult current medical literature and guidelines for clinical decisions.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSearch className="h-4 w-4" />
                Medical Content Validation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-end">
                <Button variant="outline" onClick={loadSampleContent}>
                  Load Sample Content
                </Button>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Medical Content to Validate</label>
                <Textarea
                  placeholder="Enter medical content (guidelines, protocols, treatment plans, etc.) for accuracy validation..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={15}
                  className="resize-none"
                />
              </div>

              <Button 
                onClick={validateContent} 
                disabled={isValidating || !content.trim()}
                className="w-full flex items-center gap-2"
              >
                <Brain className="h-4 w-4" />
                {isValidating ? "Validating..." : "Validate Medical Content"}
              </Button>
            </CardContent>
          </Card>

          {validation && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Stethoscope className="h-4 w-4" />
                  Medical Validation Results
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    {getStatusIcon(validation.overall)}
                    <Badge variant={getStatusColor(validation.overall) as any} className="text-lg px-4 py-2">
                      {validation.overall.toUpperCase()}
                    </Badge>
                  </div>
                  <div className={`text-2xl font-bold ${getConfidenceColor(validation.confidence)}`}>
                    {validation.confidence}% Confidence
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h4 className="font-medium">Fact Checks</h4>
                  {validation.factChecks.map((check, index) => (
                    <div key={index} className="p-3 border rounded-lg">
                      <div className="flex items-start gap-2 mb-2">
                        {getStatusIcon(check.status)}
                        <div className="flex-1">
                          <p className="text-sm font-medium">{check.claim}</p>
                          <Badge variant={getStatusColor(check.status) as any} className="mt-1">
                            {check.status}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground ml-6">
                        {check.explanation}
                      </p>
                    </div>
                  ))}
                </div>

                <Separator />

                <div className="space-y-4">
                  <h4 className="font-medium">Medical Terminology</h4>
                  
                  {validation.terminologyCheck.correct.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-success mb-2">Correct Usage</h5>
                      <div className="flex flex-wrap gap-1">
                        {validation.terminologyCheck.correct.map((term, index) => (
                          <Badge key={index} variant="default" className="text-xs">
                            {term}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {validation.terminologyCheck.incorrect.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-destructive mb-2">Incorrect Usage</h5>
                      <div className="flex flex-wrap gap-1">
                        {validation.terminologyCheck.incorrect.map((term, index) => (
                          <Badge key={index} variant="destructive" className="text-xs">
                            {term}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {validation.terminologyCheck.suggestions.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-primary mb-2">Suggested Improvements</h5>
                      <ul className="space-y-1">
                        {validation.terminologyCheck.suggestions.map((suggestion, index) => (
                          <li key={index} className="text-sm text-muted-foreground">
                            • {suggestion}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <Separator />

                <div className="space-y-4">
                  <h4 className="font-medium">Guidelines Compliance</h4>
                  {validation.guidelinesCompliance.map((guideline, index) => (
                    <div key={index} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">{guideline.standard}</span>
                        <Badge variant={guideline.compliant ? "default" : "destructive"}>
                          {guideline.compliant ? "Compliant" : "Non-Compliant"}
                        </Badge>
                      </div>
                      {guideline.issues.length > 0 && (
                        <ul className="space-y-1">
                          {guideline.issues.map((issue, issueIndex) => (
                            <li key={issueIndex} className="text-sm text-muted-foreground flex items-start gap-2">
                              <AlertTriangle className="h-3 w-3 text-warning mt-0.5 flex-shrink-0" />
                              {issue}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>

                {validation.recommendations.length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <h4 className="font-medium text-primary">Recommendations</h4>
                      <ul className="space-y-1">
                        {validation.recommendations.map((recommendation, index) => (
                          <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                            <CheckCircle className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                            {recommendation}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSearch className="h-4 w-4" />
                Recent Validations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentValidations && recentValidations.length > 0 ? (
                recentValidations.map((val) => (
                  <div key={val.id} className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0">
                        <Badge variant={getStatusColor(val.overall_status) as any} className="mb-1">
                          {val.overall_status}
                        </Badge>
                        <p className="text-xs text-muted-foreground">
                          {new Date(val.validated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`text-xs ${getConfidenceColor(val.confidence_score)}`}>
                          {val.confidence_score}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No validations yet
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
